// Reusable email preview component. Shows the EXACT branded email that
// recipients will see — full HTML documents (branded wrapper, guide hooks)
// render in a sandboxed iframe; plain markdown falls back to inline HTML.
//
// Substitutes {{token}} variables with sample values so the preview reflects
// real-world rendering (e.g. {{firstName}} → "Maria"). Sample values come
// from the sender; defaults provided so the component is safe to drop in
// anywhere even without configured samples.
//
// Used by: sequences step editor, workflows step editor, inbox composer,
// forms success message editor, templates gallery row click, admin
// templates editor (the original surface that already had preview).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, Code, Mail } from "lucide-react";
import { cn } from "@/lib/utils";

export interface EmailPreviewProps {
  /** Subject line (with {{tokens}} unresolved). Optional — if omitted, no
   *  subject pane is shown. */
  subject?: string;
  /** Markdown body (preferred). If omitted, html is used. */
  bodyMarkdown?: string | null;
  /** Pre-rendered HTML body. Used when caller already has HTML (e.g.
   *  the API's rendered body_html for sequence steps). Mutually exclusive
   *  with markdown for rendering — if both provided, html wins. */
  bodyHtml?: string | null;
  /** Sample variable values for {{token}} substitution. Common keys:
   *  firstName, lastName, email, company, services, referralSource,
   *  senderName, senderSignature, currentDate, projectName. */
  sampleVars?: Record<string, string>;
  /** When true, default to side-by-side source + preview. When false,
   *  shows only the rendered preview. */
  showSourceToggle?: boolean;
  /** Additional className for the outer container. */
  className?: string;
  /** When true, removes the inner max-height scroll cap on the body
   *  preview. Use inside a Dialog (or any parent that already provides
   *  scroll), so the dialog has a single, predictable scroll container
   *  instead of nested ones competing for the wheel event. */
  noInnerScroll?: boolean;
}

const DEFAULT_SAMPLES: Record<string, string> = {
  firstName: "Maria",
  lastName: "Castillo",
  fullName: "Maria Castillo",
  email: "maria@stellarcpa.example",
  company: "Stellar CPA Inc.",
  services: " managed IT and Microsoft 365",
  referralSource: " (referred by Acme CPA)",
  senderName: "Jose Tadeo",
  senderSignature: '<div style="color:#666;margin-top:16px;">— Jose Tadeo<br/>LuzGrid Tech<br/>jose@luzgridtech.com</div>',
  currentDate: new Date().toLocaleDateString("en-US", { dateStyle: "long" }),
  projectName: "Onboarding 2026",
};

function substituteTokens(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => vars[key] ?? "");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** Detect whether resolved HTML is a full document (<!doctype or <html)
 *  that needs an iframe rather than dangerouslySetInnerHTML. */
function isFullHtmlDocument(html: string): boolean {
  const trimmed = html.trimStart().toLowerCase();
  return trimmed.startsWith("<!doctype") || trimmed.startsWith("<html");
}

// Mirror of the cold-sequence engine's markdownToHTML (engine.ts) so
// the preview shows what the recipient actually receives in their inbox —
// including markdown links, italics, and the "→" glyph. Was previously
// a stripped-down version that only handled bold + paragraphs, which
// caused [text](url) links to render as raw markdown in the preview.
function renderMarkdownBody(md: string): string {
  const escaped = escapeHtml(md);
  return escaped
    // Bold first (greedy — must run before italic so **strong** doesn't
    // get partial italic conversion mid-token)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    // Italic
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Markdown links: [text](url) → <a href="url">text</a>. Color matches
    // the engine's link tint (cyan-ish) so preview matches sent.
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#0891b2;text-decoration:underline;" target="_blank" rel="noopener noreferrer">$1</a>')
    // Right-arrow glyph normalization (matches engine output)
    .replace(/→/g, "&#8594;")
    // Paragraph + line-break wrapping
    .split(/\n{2,}/)
    .map((p) => `<p style="margin:0 0 14px 0;">${p.replace(/\n/g, "<br/>")}</p>`)
    .join("\n");
}

/** Auto-sizing iframe for full HTML email previews. Uses postMessage from
 *  an injected script inside the iframe to resize the container to match
 *  the document height, avoiding fixed heights or scrollbars. */
function IframePreview({ html, noInnerScroll }: { html: string; noInnerScroll?: boolean }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(600);

  // Inject a resize-reporter script into the HTML document so the iframe
  // posts its content height back to the parent. This avoids cross-origin
  // restrictions since srcdoc shares the parent origin.
  const srcdoc = useMemo(() => {
    const resizeScript = `<script>
      function reportHeight() {
        var h = document.documentElement.scrollHeight || document.body.scrollHeight;
        window.parent.postMessage({ __emailPreviewHeight: h }, '*');
      }
      window.addEventListener('load', reportHeight);
      window.addEventListener('resize', reportHeight);
      // Poll briefly for webfont / image loads
      var n = 0;
      var iv = setInterval(function() { reportHeight(); if (++n > 10) clearInterval(iv); }, 200);
    </script>`;
    // Insert before </body> if present, otherwise append
    if (html.includes("</body>")) {
      return html.replace("</body>", resizeScript + "</body>");
    }
    return html + resizeScript;
  }, [html]);

  const onMessage = useCallback((e: MessageEvent) => {
    if (e.data?.__emailPreviewHeight) {
      setHeight(Math.max(200, e.data.__emailPreviewHeight + 10));
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onMessage]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcdoc}
      title="Email preview"
      sandbox="allow-same-origin"
      style={{
        width: "100%",
        height,
        border: "none",
        display: "block",
        maxHeight: noInnerScroll ? undefined : "60vh",
        overflow: noInnerScroll ? undefined : "auto",
      }}
    />
  );
}

export function EmailPreview({
  subject, bodyMarkdown, bodyHtml, sampleVars, showSourceToggle = true, className, noInnerScroll = false,
}: EmailPreviewProps) {
  const [mode, setMode] = useState<"preview" | "source">("preview");
  const vars = useMemo(() => ({ ...DEFAULT_SAMPLES, ...(sampleVars ?? {}) }), [sampleVars]);

  const resolvedSubject = useMemo(
    () => subject ? substituteTokens(subject, vars) : null,
    [subject, vars],
  );

  const resolvedBodyHtml = useMemo(() => {
    // HTML body wins if both supplied (caller's explicit choice).
    if (bodyHtml != null && bodyHtml.trim()) {
      // Strip <!--HTML--> sentinel if present
      let html = bodyHtml.trimStart().startsWith("<!--HTML-->")
        ? bodyHtml.replace(/^<!--HTML-->\s*/, "")
        : bodyHtml;
      return substituteTokens(html, vars);
    }
    if (bodyMarkdown != null) {
      // Prebuilt HTML (guide hook emails) — strip sentinel and use as-is
      if (bodyMarkdown.trimStart().startsWith("<!--HTML-->")) {
        return substituteTokens(bodyMarkdown.replace(/^<!--HTML-->\s*/, ""), vars);
      }
      return renderMarkdownBody(substituteTokens(bodyMarkdown, vars));
    }
    return "";
  }, [bodyHtml, bodyMarkdown, vars]);

  const useIframe = useMemo(() => isFullHtmlDocument(resolvedBodyHtml), [resolvedBodyHtml]);
  const sourceContent = bodyMarkdown ?? bodyHtml ?? "";

  return (
    <div className={cn("rounded-lg border border-line bg-surface-panel", className)}>
      <div className="flex items-center justify-between gap-2 border-b border-line bg-surface-panel2 px-3 py-1.5">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-dim">
          <Mail className="h-3 w-3" />
          Email preview
        </div>
        {showSourceToggle && (
          <div className="flex rounded border border-line">
            <button
              onClick={() => setMode("preview")}
              className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium transition-colors",
                mode === "preview" ? "bg-surface-active text-ink" : "text-ink-muted hover:text-ink",
              )}
              title="Rendered as the recipient sees it"
            >
              <Eye className="h-3 w-3" /> Rendered
            </button>
            <button
              onClick={() => setMode("source")}
              className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium transition-colors",
                mode === "source" ? "bg-surface-active text-ink" : "text-ink-muted hover:text-ink",
              )}
              title="Raw markdown / HTML source with tokens unresolved"
            >
              <Code className="h-3 w-3" /> Source
            </button>
          </div>
        )}
      </div>

      {mode === "source" ? (
        <pre className={cn(
          "whitespace-pre-wrap p-3 font-mono text-[11px] text-ink",
          // When inside a parent that handles scroll (e.g. a Dialog), don't
          // create a nested scroll container — wheel events get hijacked.
          !noInnerScroll && "max-h-96 overflow-auto scrollbar-thin",
        )}>
          {sourceContent || <span className="text-ink-dim">(empty body)</span>}
        </pre>
      ) : (
        <div className={cn(
          "bg-white",
          // Full-document iframes handle their own padding/layout; only add
          // padding for inline HTML fragments.
          useIframe ? "p-0" : "p-4",
          !noInnerScroll && !useIframe && "max-h-[60vh] overflow-auto scrollbar-thin",
        )}>
          {resolvedSubject != null && (
            <div className={cn("mb-3 border-b border-line pb-2", useIframe && "px-4 pt-4")}>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-dim">Subject</div>
              <div className="mt-0.5 text-sm font-semibold text-ink">{resolvedSubject}</div>
            </div>
          )}
          {useIframe ? (
            /* Full HTML document (branded wrapper / guide hook) — render in
               a sandboxed iframe so the complete document renders correctly
               with its own <head>, fonts, and inline styles. */
            <IframePreview html={resolvedBodyHtml} noInnerScroll={noInnerScroll} />
          ) : (
            /* Inline HTML fragment — render directly in a styled container.
               Used for simple markdown bodies, autoresponders, etc. */
            <>
              <div
                style={{
                  fontFamily: "-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
                  maxWidth: 560,
                  margin: "0 auto",
                  padding: 0,
                  color: "#0f172a",
                  lineHeight: 1.6,
                  fontSize: 15,
                }}
                dangerouslySetInnerHTML={{ __html: resolvedBodyHtml || '<p style="color:#94a3b8;">(empty body)</p>' }}
              />
              {Object.keys(sampleVars ?? {}).length === 0 && (resolvedBodyHtml.includes("Maria") || resolvedBodyHtml.includes("Castillo")) && (
                <p className="mx-auto mt-3 max-w-[560px] rounded bg-amber-50 px-2 py-1.5 text-[10px] text-amber-800">
                  Showing default sample values (Maria Castillo etc.). Real recipients see their own data substituted.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
